import React, { Suspense, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ACTIVE_ORG_STORAGE_KEY } from './components/OrgSwitcher';
import { reportClientError } from './utils/errorReporter';
import { lazyWithRetry } from './utils/lazyWithRetry';

// ── Route-level lazy bundles ─────────────────────────────────────────────────
// Each import() becomes a separate webpack chunk so the initial page load only
// fetches the chunk for the route the user actually visits.
const LoginPage              = lazyWithRetry(() => import(/* webpackPrefetch: true */ './pages/LoginPage'));
const ChatPage               = lazyWithRetry(() => import(/* webpackPrefetch: true */ './pages/ChatPage'));
const ApprovalPage           = lazyWithRetry(() => import('./pages/ApprovalPage'));
const DispatchApprovalPage   = lazyWithRetry(() => import('./pages/DispatchApprovalPage'));
const CollabApprovalPage     = lazyWithRetry(() => import('./pages/CollabApprovalPage'));
const HealthMonitorPage      = lazyWithRetry(() => import('./pages/HealthMonitorPage'));
const AttachmentViewerPage = lazyWithRetry(() => import('./pages/AttachmentViewerPage'));
const DesignViewerPage       = lazyWithRetry(() => import('./pages/DesignViewerPage'));

// Admin shell + all its section pages
const AdminShell                 = lazyWithRetry(() => import(/* webpackPrefetch: true */ './pages/admin/AdminShell'));
const DashboardSection           = lazyWithRetry(() => import('./pages/admin/sections/DashboardSection'));
const UsersManagement            = lazyWithRetry(() => import('./pages/admin/UsersManagement'));
const SettingsSection            = lazyWithRetry(() => import('./pages/admin/sections/SettingsSection'));
const ScheduledTasksSection      = lazyWithRetry(() => import('./pages/admin/sections/ScheduledTasksSection'));
const OperationsSection          = lazyWithRetry(() => import('./pages/admin/sections/OperationsSection'));
const AgentIssuesSection         = lazyWithRetry(() => import('./pages/admin/sections/AgentIssuesSection'));
const ExportTemplatesSection     = lazyWithRetry(() => import('./pages/admin/sections/ExportTemplatesSection'));
const TestServerSection          = lazyWithRetry(() => import('./pages/admin/sections/TestServerSection'));
const ConductorSlotsSection      = lazyWithRetry(() => import('./pages/admin/sections/ConductorSlotsSection'));
const DeploymentSlotsSection     = lazyWithRetry(() => import('./pages/admin/sections/DeploymentSlotsSection'));
const ProvidersSection           = lazyWithRetry(() => import('./pages/admin/sections/ProvidersSection'));
const ModelsRoutingSection       = lazyWithRetry(() => import('./pages/admin/sections/ModelsRoutingSection'));
const SubscriptionPricingSection = lazyWithRetry(() => import('./pages/admin/sections/SubscriptionPricingSection'));
const PlanQuotaConfigSection     = lazyWithRetry(() => import('./pages/admin/sections/PlanQuotaConfigSection'));
const ModelPricingSection        = lazyWithRetry(() => import('./pages/admin/sections/ModelPricingSection'));
const OrgSettings                = lazyWithRetry(() => import('./pages/admin/OrgSettings'));

const PlatformAdminPanel   = lazyWithRetry(() => import('./pages/PlatformAdminPanel'));
const OnboardingPage       = lazyWithRetry(() => import('./pages/OnboardingPage'));
const OnboardingSetupWizard = lazyWithRetry(() => import('./pages/OnboardingSetupWizard'));
const AcceptInvitePage     = lazyWithRetry(() => import('./pages/AcceptInvitePage'));
const ForgotPasswordPage   = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage    = lazyWithRetry(() => import('./pages/ResetPasswordPage'));

/**
 * Phase 3 T6a (P-COND-T6-PLAN) — OrgShell IA foundation.
 * Lazy-loaded so the legacy `/admin/*` routes and ChatPage do not pay
 * the bundle cost until the user navigates into the new `/orgs/:orgId/*`
 * namespace. T6b workers will add nested view routes underneath; for now
 * the shell renders an empty <Outlet />.
 */
const OrgShell = lazyWithRetry(() => import('./pages/org/OrgShell'));

// Phase 3 T6d wiring (P-COND-T6-PLAN): nested view components for OrgShell.
// Each was authored in T6b but never wired as a child <Route> below — until
// now, OrgShell rendered an empty <Outlet />, producing blank pages for 8 of
// the 9 sidebar items. Lazy-loaded to keep the legacy ChatPage bundle clean.
// `OrgSettings` is aliased to `OrgSettingsPage` to avoid collision with the
// legacy admin OrgSettings import at line 23.
const OrgOverview      = lazyWithRetry(() => import('./pages/org/OrgOverview'));
const OrgTokenUsage    = lazyWithRetry(() => import('./pages/org/OrgTokenUsage'));
const OrgMembers       = lazyWithRetry(() => import('./pages/org/OrgMembers'));
const OrgProjects      = lazyWithRetry(() => import('./pages/org/OrgProjects'));
const ProjectDetail    = lazyWithRetry(() => import('./pages/org/ProjectDetail'));
const OrgAudit         = lazyWithRetry(() => import('./pages/org/OrgAudit'));
const OrgSubscription  = lazyWithRetry(() => import('./pages/org/OrgSubscription'));
const OrgSettingsPage  = lazyWithRetry(() => import('./pages/org/OrgSettings'));
const Profile          = lazyWithRetry(() => import('./pages/org/Profile'));
const PlatformOrgsList = lazyWithRetry(() => import('./pages/platform/PlatformOrgsList'));
// P-COND-BF8D90F5182B ST-05: user-facing scheduled tasks management page.
const ScheduledTasksPage = lazyWithRetry(() => import('./pages/ScheduledTasksPage'));
// SKC-T1.3 (P-COND-B13A4C16E0A2) — Skill Center route skeleton. Placeholder
// pages only; no entry links point here yet (entries land in SKC-T3.3+ and are
// gated on GET /users/me/skills/capabilities).
const SkillCenter = lazyWithRetry(() => import('./pages/skills/SkillCenter'));
const SkillCreate = lazyWithRetry(() => import('./pages/skills/SkillCreate'));
const OrgSkills   = lazyWithRetry(() => import('./pages/org/OrgSkills'));
// M3-3 (P-COND-ACDA06F07BFD) — MCP 中心 (`/mcp`), OrgShell-mounted like /skills.
const McpCenter   = lazyWithRetry(() => import('./pages/mcp/McpCenter'));
// Conductor Desktop panels (40-tier3-integration-ui.md §1.3). T3-B pre-placed
// these three as the single mount point so that three agents building the
// components in parallel would never edit App.tsx concurrently — the last to
// commit would have silently overwritten the other two.
const DesktopDevicePanel = lazyWithRetry(() => import('./components/DesktopDevicePanel'));
const DesktopGrantPanel = lazyWithRetry(() => import('./components/DesktopGrantPanel'));
const DesktopAuditLogView = lazyWithRetry(() => import('./components/DesktopAuditLogView'));
const DesktopDownloadPage = lazyWithRetry(() => import('./pages/DesktopDownloadPage'));
const DesktopLink = lazyWithRetry(() => import('./pages/DesktopLink'));
const DesktopConnect = lazyWithRetry(() => import('./pages/DesktopConnect'));

// ── ChunkErrorBoundary ───────────────────────────────────────────────────────
// Catches dynamic-import failures (network drop mid-load, CDN miss, stale hash
// after a redeploy). Without this, a failed chunk leaves the user on a blank
// "Loading…" screen forever. Offers a reload button as the recovery path.
// Last resort only: utils/lazyWithRetry.ts first retries the import (1s, 3s)
// and then auto-reloads once per 60s; only what survives that lands here.
interface ChunkErrorBoundaryState { hasError: boolean; }
class ChunkErrorBoundary extends Component<{ children: ReactNode }, ChunkErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_: Error): ChunkErrorBoundaryState {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ChunkErrorBoundary]', error, info);
    reportClientError(error, {
      kind: 'ChunkErrorBoundary',
      componentStack: info.componentStack ?? undefined,
    });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', height: '100vh', gap: '12px',
                      fontFamily: 'sans-serif', color: '#374151' }}>
          <div style={{ fontSize: '16px' }}>页面加载失败，请刷新重试</div>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none',
                     background: '#2563eb', color: '#fff', cursor: 'pointer',
                     fontSize: '14px' }}
          >
            刷新页面
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Phase 3 T6a R2 mitigation — redirect legacy bookmark
 * `/admin/orgs/:orgId` to the new `/orgs/:orgId/settings` route. The
 * legacy `OrgSettings.tsx` (mounted at the same path inside AdminShell
 * below) is kept intact per §5.5 write-locked rule — this redirect lives
 * OUTSIDE the legacy AdminShell tree so it short-circuits before the
 * AdminShell route matches.
 */
const LegacyOrgSettingsRedirect: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  return <Navigate to={`/orgs/${orgId}/settings`} replace />;
};

/**
 * Phase 4 W5 (P-COND-P4) — legacy `/admin/projects` retirement.
 * Project management reached parity on the org-scoped surface
 * (`/orgs/:orgId/projects` + `ProjectDetail`). This redirect routes the
 * retired admin route to the active org's projects view; when no active
 * org is selected it falls back to the platform org list so the user can
 * pick one. Mirrors the LegacyOrgSettingsRedirect short-circuit pattern.
 */
const LegacyAdminProjectsRedirect: React.FC = () => {
  const activeOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  return <Navigate to={activeOrgId ? `/orgs/${activeOrgId}/projects` : '/admin/orgs'} replace />;
};

/**
 * Protected route wrapper.
 * Redirects unauthenticated users to the login page.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

/**
 * Admin route wrapper.
 * Redirects non-admin or unauthenticated users to chat.
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, authResolved } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  // Wait for the initial admin resolution before deciding. On a reload or
  // deep-link into /admin, `isAdmin` is seeded to a safe `false` until
  // `getMe()` returns; redirecting during that window bounces a legitimate
  // platform admin back to chat (the "管理面板 不能跳转" symptom).
  if (!authResolved) return null;
  if (!isAdmin) return <Navigate to="/" />;
  return <>{children}</>;
};

const LOADING_FALLBACK = (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100vh', color: '#9ca3af', fontFamily: 'sans-serif',
                fontSize: '14px' }}>
    加载中…
  </div>
);

/**
 * Main application component.
 * Sets up authentication context, routing, and global providers.
 */
const App: React.FC = () => (
  <AuthProvider>
    <LanguageProvider>
    <BrowserRouter basename={process.env.PUBLIC_URL || '/conductor'}>
      <ChunkErrorBoundary>
        <Suspense fallback={LOADING_FALLBACK}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Sub-Task 4 (user-org-identity-ux-extension) — PUBLIC invite
                onboarding page. A brand-new user (no account) redeems an
                invite code here to create their platform user + join the
                inviting org. Declared OUTSIDE ProtectedRoute so it is
                reachable without auth, mirroring `/login` above. */}
            <Route path="/invite/:code" element={<AcceptInvitePage />} />
            {/* P-COND-95FF6F1582EC — PUBLIC password-reset flow. Both routes are
                declared OUTSIDE ProtectedRoute so an unauthenticated user who
                forgot their password can reach them (mirrors `/invite/:code`). */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Phase 3 T6a (P-COND-T6-PLAN) R2 mitigation — legacy
                `/admin/orgs/:orgId` bookmark redirects to new
                `/orgs/:orgId/settings`. Mounted ABOVE the legacy `/admin`
                parent route so the redirect short-circuits before the nested
                `orgs/:orgId` element resolves to OrgSettings.tsx. */}
            <Route
              path="/admin/orgs/:orgId"
              element={
                <ProtectedRoute>
                  <LegacyOrgSettingsRedirect />
                </ProtectedRoute>
              }
            />
            {/* Phase 3 T6a (P-COND-T6-PLAN) — new OrgShell IA namespace.
                T6d wired the nested view routes (overview/members/projects/
                audit/settings + projects/:projectId). The shell's <Outlet />
                renders the matched child below; bare /orgs/:orgId redirects
                to /overview. */}
            <Route
              path="/orgs/:orgId"
              element={
                <ProtectedRoute>
                  <OrgShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<OrgOverview />} />
              <Route path="usage" element={<OrgTokenUsage />} />
              <Route path="subscription" element={<OrgSubscription />} />
              <Route path="members" element={<OrgMembers />} />
              <Route path="projects" element={<OrgProjects />} />
              <Route path="projects/:projectId" element={<ProjectDetail />} />
              <Route path="audit" element={<OrgAudit />} />
              {/* SKC-T1.3 — org skills / publish approvals (?tab=library|approvals).
                  T7 ST3: with skill_center_enabled OrgSkills redirects to
                  `/skills?tab=org&sub=library|approvals` (org kept active);
                  with it off the legacy page renders unchanged. */}
              <Route path="skills" element={<OrgSkills />} />
              {/* Conductor Desktop (Tier 3). Path naming is declared by T3-B per
                  §1.3 and matched verbatim by the OrgShell sidebar entries. */}
              <Route path="desktop/devices" element={<DesktopDevicePanel />} />
              <Route path="desktop/grants" element={<DesktopGrantPanel />} />
              <Route path="desktop/audit" element={<DesktopAuditLogView />} />
              <Route path="settings" element={<OrgSettingsPage />} />
            </Route>
            {/* Phase 3 T6a — `/profile` mounts OrgShell so the sidebar's
                "账号" section stays visible alongside the profile view.
                T6d wired Profile.tsx as the nested child. */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <OrgShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Profile />} />
            </Route>
            {/* P-COND-BF8D90F5182B ST-05 — `/scheduled-tasks` mounts OrgShell so
                the sidebar stays visible, consistent with the `/profile` pattern. */}
            <Route
              path="/scheduled-tasks"
              element={
                <ProtectedRoute>
                  <OrgShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<ScheduledTasksPage />} />
            </Route>
            {/* SKC-T1.3 (P-COND-B13A4C16E0A2) — Skill Center mounts OrgShell,
                same pattern as `/profile` and `/scheduled-tasks`.
                `/skills?tab=library|mine|project|org&sub=&from_project=&from_conv=&mode=`
                (T7 ST3; legacy `?tab=guide` opens the help dialog); `/skills/new`. */}
            <Route
              path="/skills"
              element={
                <ProtectedRoute>
                  <OrgShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<SkillCenter />} />
              <Route path="new" element={<SkillCreate />} />
            </Route>
            {/* M3-3 — MCP 中心: `/mcp?tab=effective|requests|org|catalog|inbox&project=&open=`;
                the OAuth callback returns here with `?mcp_oauth=`. Flag off →
                the page renders 「尚未开放」 (backend 404s every MCP route). */}
            <Route
              path="/mcp"
              element={
                <ProtectedRoute>
                  <OrgShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<McpCenter />} />
            </Route>
            {/* Phase 3 T6a — `/admin/orgs` (platform-admin list of all
                orgs). T6d wired PlatformOrgsList.tsx as the nested child.
                Gated by AdminRoute so only platform_admin can reach it. */}
            <Route
              path="/admin/orgs"
              element={
                <AdminRoute>
                  <OrgShell />
                </AdminRoute>
              }
            >
              <Route index element={<PlatformOrgsList />} />
            </Route>
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminShell />
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardSection />} />
              <Route path="health" element={<HealthMonitorPage />} />
              {/* Phase 4 W5: legacy ProjectsSection retired — redirect to the
                  org-scoped projects parity surface. */}
              <Route path="projects" element={<LegacyAdminProjectsRedirect />} />
              {/* Phase 4 W5: UsersPermissionsSection replaced in-place by the
                  org-architecture platform UsersManagement page (parity). */}
              <Route path="users" element={<UsersManagement />} />
              <Route path="settings" element={<SettingsSection />} />
              <Route path="pricing" element={<SubscriptionPricingSection />} />
              <Route path="plan-quota" element={<PlanQuotaConfigSection />} />
              <Route path="model-pricing" element={<ModelPricingSection />} />
              <Route path="scheduled" element={<ScheduledTasksSection />} />
              <Route path="operations" element={<OperationsSection />} />
              <Route path="agent-issues" element={<AgentIssuesSection />} />
              <Route path="export" element={<ExportTemplatesSection />} />
              <Route path="test-server" element={<TestServerSection />} />
              <Route path="conductor-slots" element={<ConductorSlotsSection />} />
              <Route path="deployment-slots" element={<DeploymentSlotsSection />} />
              <Route path="providers" element={<ProvidersSection />} />
              {/* Sub-Task C (P-COND-B0ACC698DDB7): /admin/models replaces the */}
              {/* split surfaces (legacy default-model card, advanced overrides, */}
              {/* backend-selector). The unified page consumes Sub-Task A's catalog */}
              {/* SSoT via GET /api/admin/models. */}
              <Route path="models" element={<ModelsRoutingSection />} />
              {/* Sub-Task B (P-COND-B0ACC698DDB7): /admin/backend-selector deleted; */}
              {/* 301-style redirect to the unified /admin/models. */}
              <Route path="backend-selector" element={<Navigate to="/admin/models" replace />} />
              {/* Sub-Task C (P-COND-B0ACC698DDB7): /admin/tokens deleted; absorbed */}
              {/* by /admin/providers (single token table). Bookmarks 301 to providers. */}
              <Route path="tokens" element={<Navigate to="/admin/providers" replace />} />
              {/* Phase 1 T6-B: Org Settings detail page (per-org admin). */}
              <Route path="orgs/:orgId" element={<OrgSettings />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Route>
            {/* Phase 3 v1.1 T4 (lane (f)): platform-admin quota / usage panel.
                Mounted outside `/admin/*` so it doesn't render inside AdminShell
                (which is built around the legacy AdminPage section model). The
                page has its own topbar + tabs and is platform-admin-gated. */}
            <Route
              path="/admin/platform"
              element={
                <AdminRoute>
                  <PlatformAdminPanel />
                </AdminRoute>
              }
            />
            {/* Phase 3 v1.1 T4 (lane (g)): three-pane onboarding chooser
                for users with zero org memberships. ProtectedRoute gates
                on auth; the page itself bounces back to "/" when the user
                already has ≥ 1 org membership (handles deep-link visits). */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            {/* Phase B PB-1A (P-COND-54619973CED8-PB-T1-1) — post-registration
                guided setup wizard. Mounted AFTER /onboarding and BEFORE the
                /* catch-all so route resolution is unambiguous.
                See planning/onboarding-phase-b-execution-plan.md §3 PB-1A. */}
            <Route
              path="/onboarding/setup"
              element={
                <ProtectedRoute>
                  <OnboardingSetupWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <AdminRoute>
                  <ApprovalPage />
                </AdminRoute>
              }
            />
            <Route
              path="/dispatch-approvals"
              element={
                <ProtectedRoute>
                  <DispatchApprovalPage />
                </ProtectedRoute>
              }
            />
            <Route path="/attachments/:conversationId/:attachmentId" element={<ProtectedRoute><AttachmentViewerPage /></ProtectedRoute>} />
            {/* Conductor Desktop installer downloads. TOP-LEVEL on purpose: the
                installer is not an org resource (the backend routes are guarded by
                `get_current_user` alone), so nesting it under `/orgs/:orgId` would
                invent an org scope the API does not have. `ProtectedRoute`, never
                `AdminRoute` — any logged-in user may download the client
                (business decision #5). */}
            <Route
              path="/desktop/download"
              element={
                <ProtectedRoute>
                  <DesktopDownloadPage />
                </ProtectedRoute>
              }
            />
            {/* Conductor Desktop browser-loopback authorization (Stage B,
                T-DESK-B-WEB). Deliberately NOT wrapped in `ProtectedRoute`: that
                wrapper redirects to a bare `/login`, which throws away the query
                string — and here the query string IS the authorization request
                (`claim_token` / `state` / `port`). The page runs the same
                `isAuthenticated` check itself, after stashing its return target.
                Top-level like `/desktop/download`, and for the same reason: a
                device binding is a user-level resource with no org scope. */}
            <Route path="/desktop/link" element={<DesktopLink />} />
            {/* Desktop 「登录」 browser enroll (P-COND-6A4AAA40F3A1). Same
                self-gated auth as /desktop/link — the query is the request. */}
            <Route path="/desktop/connect" element={<DesktopConnect />} />
            {/* Collab Approval Center (Workspace Modes 方案3). ProtectedRoute
                gates auth; the page's own useOrgRole().canManage guard enforces
                the org admin/owner access (matching the backend authz). */}
            <Route
              path="/collab-approvals"
              element={
                <ProtectedRoute>
                  <CollabApprovalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/design/:projectName/*"
              element={
                <ProtectedRoute>
                  <DesignViewerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </BrowserRouter>
    </LanguageProvider>
  </AuthProvider>
);

export default App;
